import { useEffect, useRef, useState } from 'react';
import type { DeficiencyEntry, EntrySeverity } from '../../api/deficiency-entries';
import { Icon } from '../templates/icons';

interface Props {
  entry: DeficiencyEntry;
  onPatch: (patch: {
    title?: string;
    description?: string | null;
    severity?: EntrySeverity;
    component_name?: string | null;
    trade_tag?: string | null;
  }) => void;
  onClose: () => void;
}

const SEVERITY_OPTS: Array<{ value: EntrySeverity; label: string; pillClass: string }> = [
  { value: 'safety_concern', label: 'Safety Concern', pillClass: 'dl-sev-pill dl-sev-pill--safety' },
  { value: 'recommendation', label: 'Recommendation', pillClass: 'dl-sev-pill dl-sev-pill--rec' },
  { value: null, label: 'Observation', pillClass: 'dl-sev-pill dl-sev-pill--none' },
];

export function EntryDrawer({ entry, onPatch, onClose }: Props) {
  const [title, setTitle] = useState(entry.title);
  const [description, setDescription] = useState(entry.description ?? '');
  const [componentName, setComponentName] = useState(entry.componentName ?? '');
  const [tradeTag, setTradeTag] = useState(entry.tradeTag ?? '');
  const committed = useRef({
    title: entry.title,
    description: entry.description ?? '',
    componentName: entry.componentName ?? '',
    tradeTag: entry.tradeTag ?? '',
  });

  useEffect(() => {
    setTitle(entry.title);
    setDescription(entry.description ?? '');
    setComponentName(entry.componentName ?? '');
    setTradeTag(entry.tradeTag ?? '');
    committed.current = {
      title: entry.title,
      description: entry.description ?? '',
      componentName: entry.componentName ?? '',
      tradeTag: entry.tradeTag ?? '',
    };
  }, [entry.id, entry.title, entry.description, entry.componentName, entry.tradeTag]);

  const commitTitle = () => {
    const next = title.trim();
    if (!next || next === committed.current.title) return;
    committed.current.title = next;
    onPatch({ title: next });
  };
  const commitDescription = () => {
    const next = description.trim();
    if (next === committed.current.description) return;
    committed.current.description = next;
    onPatch({ description: next.length === 0 ? null : next });
  };
  const commitComponent = () => {
    const next = componentName.trim();
    if (next === committed.current.componentName) return;
    committed.current.componentName = next;
    onPatch({ component_name: next.length === 0 ? null : next });
  };
  const commitTradeTag = () => {
    // Normalise to lowercase to match the server-side canonical form.
    const next = tradeTag.trim().toLowerCase();
    if (next === committed.current.tradeTag) return;
    committed.current.tradeTag = next;
    onPatch({ trade_tag: next.length === 0 ? null : next });
  };

  return (
    <div className="ts-drawer dl-drawer" onClick={(e) => e.stopPropagation()}>
      <div className="ts-drawer-head">
        <span className="ts-drawer-title">Edit entry</span>
        <span className="ts-drawer-meta">
          {entry.useCount} {entry.useCount === 1 ? 'use' : 'uses'}
        </span>
        <button
          type="button"
          className="ts-menu-btn ts-menu-btn--on"
          aria-label="Close"
          onClick={onClose}
          style={{ marginLeft: 6 }}
        >
          <Icon name="x" size={13} />
        </button>
      </div>

      <div className="dl-drawer-grid">
        <label className="dl-field">
          <span className="dl-field-label">Title</span>
          <input
            className="dl-input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            placeholder="e.g. Mortar joints show freeze–thaw cracking"
          />
        </label>

        <label className="dl-field">
          <span className="dl-field-label">Description (optional)</span>
          <textarea
            className="dl-input dl-input--textarea"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={commitDescription}
            placeholder="Detail the observation as it should read in the report."
          />
        </label>

        <div className="dl-field">
          <span className="dl-field-label">Severity</span>
          <div className="dl-sev-group" role="radiogroup" aria-label="Severity">
            {SEVERITY_OPTS.map((opt) => {
              const on = entry.severity === opt.value;
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={opt.pillClass + (on ? ' dl-sev-pill--on' : '')}
                  onClick={() => onPatch({ severity: opt.value })}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <label className="dl-field">
          <span className="dl-field-label">Component (optional)</span>
          <input
            className="dl-input"
            type="text"
            value={componentName}
            onChange={(e) => setComponentName(e.target.value)}
            onBlur={commitComponent}
            placeholder="e.g. Chimney, Roof, Water heater"
          />
        </label>

        <label className="dl-field">
          <span className="dl-field-label">Trade (optional)</span>
          <input
            className="dl-input"
            type="text"
            value={tradeTag}
            onChange={(e) => setTradeTag(e.target.value)}
            onBlur={commitTradeTag}
            placeholder="e.g. plumber, electrician, roof"
          />
        </label>
      </div>
    </div>
  );
}
